import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { add } from "date-fns";
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { setSelectedBaby } from '../../actions/ui';

import {
  FormLabel,
  FormSelect,
  FormRadio,
  FormIconChooser,
  BaseForm,
  FormInputHolder
} from '../form';
import { checkTime, required } from '../../helpers/formValidators';

import Duration, { addDateAndTimeFields, getDurationStrict } from './Duration';
import babyIcons from '../icons/babyIcons';
import ErrorMessage from '../ErrorMessage/ErrorMessage';
import { Error } from '../form/BaseForm';
import { FormSpy } from 'react-final-form';
import { PrimaryButton, SecondaryButton } from '../buttons';
import { FormContext } from '../../context/form-context';
import { OnChange } from 'react-final-form-listeners';

const SleepForm = ({
  selectedProfile = {},
  babies = [],
  profileOptions = [],
  sleepRecordsPost = () => Promise.resolve(),
  sleepRecordsPatch = () => Promise.resolve(),
  sleepRecordsList = () => Promise.resolve(), 
  accountId,
  hideTrackerModals = () => {},
  handleRecords = () => {},
  showUnSavedModal = () => {},
  showSaveEntryModal = () => {},
  showFirstTimeSavingModal = false,
  selectedBabyId = null,
  sleepModalShown = false
}) => {
  const [initialValues, setInitialValues] = useState(null);
  const formContext = useContext(FormContext);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    if (!sleepModalShown) {
      setInitialValues(null);
      return;
    }

    if (formContext.formValues && Object.keys(formContext.formValues).length > 0) {
      setInitialValues({ ...formContext.formValues });
      return;
    }

    const latestRecord = handleRecords('sleep', selectedBabyId) || {};
    setInitialValues({
      babyId: selectedBabyId,
      providerId: selectedProfile.id,
      date: new Date(),
      startTime: new Date(),
      endTime: add(new Date(), { minutes: 10 }),
      ...latestRecord,
    });
  }, [sleepModalShown, selectedBabyId]);

  const onSubmit = (values) => {
    const recordBody = {
      ...values,
      providerId: values.providerId === "other" ? null : values.providerId,
      otherProvider: values.providerId !== "other" ? null : values.otherProvider,
      date: addDateAndTimeFields(values.date, values.startTime),
      duration: getDurationStrict(values.startTime, values.endTime),
      endTime: addDateAndTimeFields(values.date, values.endTime)
    };

    const originalBabyId = initialValues?.babyId;
    const newBabyId = values.babyId || originalBabyId;

    if (values.id) {
      return sleepRecordsPatch(
        { accountId, babyId: originalBabyId, id: values.id },
        { body: JSON.stringify(recordBody) }
      )
        .then((data) => {
          if (data) {
            sleepRecordsList({ accountId, babyId: originalBabyId });
            
            if (newBabyId !== originalBabyId) {
              sleepRecordsList({ accountId, babyId: newBabyId });
            }
            hideTrackerModals();
          }
        })
        .catch(() => <ErrorMessage />); 
    }

    return sleepRecordsPost(
      { accountId, babyId: newBabyId },
      { body: JSON.stringify(recordBody) }
    )
      .then((data) => {
        if (data) {
          sleepRecordsList({ accountId, babyId: newBabyId });
          hideTrackerModals();
          dispatch(setSelectedBaby(newBabyId));
          navigate('/dashboard/sleep');
        }
      })
      .catch(() => <ErrorMessage />);
  };

  const onSave = (values) => {
    if (!showFirstTimeSavingModal) return onSubmit(values);
    formContext.setValues(values);
    formContext.setFunctions({ onSubmit });
    return showSaveEntryModal();
  };

  if (!sleepModalShown || !initialValues) return null;

  return (
    <BaseForm 
      key={initialValues.id || 'new'} 
      className="sleepFormModal" 
      onSubmit={onSave} 
      validate={checkTime} 
      initialValues={initialValues}
    >
      <h2 className="title">Track Sleep</h2>

      {babies.length > 1 && !initialValues?.id && (
        <React.Fragment>
          <FormIconChooser>
            {babies.map((baby) => (
              <FormRadio key={baby.id} value={baby.id} name="babyId" imageSrc={babyIcons[baby.icon]} validate={required}>
                {baby.name}
              </FormRadio>
            ))}
          </FormIconChooser>
          <OnChange name="babyId">
            {(value, previous) => {
              if (value === previous) return;
              const latestRecord = handleRecords('sleep', value) || {};
              setInitialValues({ ...initialValues, ...latestRecord, babyId: value });
            }}
          </OnChange>
          <Error name="babyId" />
        </React.Fragment>
      )}

      {babies && initialValues?.id && (() => {
        const editBaby = babies.find(b => b.id === initialValues.babyId);
        return editBaby ? (
          <FormIconChooser>
            <label className="form-radio form-radio--checked">
              <div className="form-radio__image-container">
                <img src={babyIcons[editBaby.icon]} alt={editBaby.name} className="form-radio__image" width="2.4rem" height="2.4rem" />
              </div>
              <span className="form-radio__label">{editBaby.name}</span>
            </label>
          </FormIconChooser>
        ) : null;
      })()}

      <FormLabel htmlFor="providerId">Provider</FormLabel>
      <FormSelect placeholder="Choose Provider..." name="providerId" options={profileOptions} includeOther otherName="otherProvider" />

      <Duration />

      <FormSpy subscription={{ submitting: true }}>
        {({ submitting }) => (
          <FormInputHolder>
            <SecondaryButton type="button" label="Cancel" onClick={() => formContext.isDirty ? showUnSavedModal() : hideTrackerModals()} />
            <PrimaryButton type="submit" label="Save" disabled={submitting} />
          </FormInputHolder>
        )}
      </FormSpy>

      <FormSpy
        subscription={{ dirty: true, visited: true, values: true }}
        onChange={({ dirty, visited, values }) => {
          if (dirty) {
            setTimeout(() => {
              formContext.setValues(values);
              const actuallyDirty = Object.values(visited).some(Boolean);
              actuallyDirty ? formContext.setDirty() : formContext.notDirty();
            }, 0);
          }
        }}
      />
    </BaseForm>
  );
};

SleepForm.propTypes = {
  selectedProfile: PropTypes.shape({ id: PropTypes.string }),
  babies: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string, name: PropTypes.string, icon: PropTypes.string })).isRequired,
  profileOptions: PropTypes.arrayOf(PropTypes.object),
  sleepRecordsPost: PropTypes.func,
  sleepRecordsPatch: PropTypes.func,
  sleepRecordsList: PropTypes.func,
  accountId: PropTypes.string,
  hideTrackerModals: PropTypes.func,
  handleRecords: PropTypes.func,
  showUnSavedModal: PropTypes.func,
  showSaveEntryModal: PropTypes.func,
  showFirstTimeSavingModal: PropTypes.bool,
  selectedBabyId: PropTypes.string,
  sleepModalShown: PropTypes.bool
};

export default SleepForm;
